import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { CircleNotch, Play } from '@phosphor-icons/react';

// FRONT-214: provisioning is async. The panel kicks off a sandbox via
// /api/sandbox/start (non-blocking) and polls /api/sandbox/status until it's
// ready — so a cold start shows "Starting sandbox…" instead of 502-ing.
type SandboxState = 'ready' | 'provisioning' | 'error' | 'absent';
interface StatusResp {
  enabled: boolean;
  state?: SandboxState;
  detail?: string | null;
  reason?: string;
}

interface RunResult {
  language: 'shell' | 'python';
  stdout: string;
  stderr: string;
  exitCode: number;
  durationMs: number;
  timedOut: boolean;
  truncated: boolean;
}

const PRESETS: Array<{ label: string; language: 'shell' | 'python'; snippet: string }> = [
  { label: 'echo hello', language: 'shell', snippet: 'echo "hello from sandbox $(uname -a)"' },
  { label: 'python sum', language: 'python', snippet: 'print(sum(range(100)))' },
  { label: 'ls /workspace', language: 'shell', snippet: 'ls -la /workspace' },
];

const POLL_MS = 2_000;
const POLL_CAP_MS = 120_000; // stop auto-polling after 2 min; user can resume

function startProvisioning(): void {
  // Non-blocking kick-off; the status poll surfaces real provisioning failures.
  // Log here so a network/auth error on the start call is still observable.
  void fetch('/api/sandbox/start', { method: 'POST', credentials: 'include' }).catch((err) => {
    console.error('[sandbox] failed to start provisioning:', err);
  });
}

export function SandboxDebug() {
  const startedRef = useRef(false);
  const [capped, setCapped] = useState(false);
  const [pollSince, setPollSince] = useState<number>(() => Date.now());
  const [pendingRun, setPendingRun] = useState(false);

  // Kick off provisioning once when the panel mounts.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startProvisioning();
  }, []);

  const status = useQuery<StatusResp>({
    queryKey: ['sandbox', 'status'],
    queryFn: async () => {
      const res = await fetch('/api/sandbox/status', { credentials: 'include' });
      return (await res.json()) as StatusResp;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.enabled === false) return false;
      if (data.state === 'ready' || data.state === 'error') return false;
      if (capped) return false;
      return POLL_MS;
    },
    retry: false,
  });

  const enabled = status.data?.enabled !== false;
  const state: SandboxState | undefined = status.data?.state;
  const isReady = state === 'ready';

  // Cap the poll so it can't spin forever. "Keep waiting" resets pollSince,
  // which restarts this timer.
  useEffect(() => {
    if (capped || !enabled) return;
    if (state === 'ready' || state === 'error') return;
    const t = setTimeout(() => setCapped(true), POLL_CAP_MS);
    return () => clearTimeout(t);
  }, [capped, enabled, state, pollSince]);

  const [snippet, setSnippet] = useState(PRESETS[0].snippet);
  const [language, setLanguage] = useState<'shell' | 'python'>(PRESETS[0].language);
  const [result, setResult] = useState<RunResult | null>(null);

  const run = useMutation({
    mutationFn: async (): Promise<RunResult> => {
      return api.post<RunResult>('/api/sandbox/run', { snippet, language, timeoutMs: 10_000 });
    },
    onSuccess: (data) => setResult(data),
    onError: (err) =>
      setResult({
        language,
        stdout: '',
        stderr: (err as Error).message,
        exitCode: -1,
        durationMs: 0,
        timedOut: false,
        truncated: false,
      }),
  });

  // Auto-fire a queued run the moment the sandbox becomes ready.
  useEffect(() => {
    if (pendingRun && isReady) {
      setPendingRun(false);
      run.mutate();
    }
  }, [pendingRun, isReady, run]);

  const resumePolling = () => {
    setCapped(false);
    setPollSince(Date.now());
    startProvisioning();
    void status.refetch();
  };

  const onRun = () => {
    if (isReady) {
      run.mutate();
      return;
    }
    // Not ready yet: queue the run and make sure provisioning is moving.
    setPendingRun(true);
    if (capped) resumePolling();
    else {
      startProvisioning();
      void status.refetch();
    }
  };

  const statusContent = (() => {
    if (!enabled) {
      return (
        <>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />
          <span className="text-muted-foreground">
            Sandbox disabled{status.data?.reason ? ` — ${status.data.reason}` : ''}
          </span>
        </>
      );
    }
    if (state === 'error') {
      return (
        <>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-destructive" />
          <span className="text-destructive truncate">
            Sandbox error{status.data?.detail ? ` — ${status.data.detail}` : ''}
          </span>
        </>
      );
    }
    if (isReady) {
      return (
        <>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
          <span className="text-foreground/80">Sandbox ready</span>
        </>
      );
    }
    // provisioning / absent / first load
    if (capped) {
      return (
        <>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
          <span className="text-muted-foreground">Still starting…</span>
          <button
            type="button"
            onClick={resumePolling}
            className="ml-1 underline underline-offset-2 text-foreground/70 hover:text-foreground"
          >
            keep waiting
          </button>
        </>
      );
    }
    return (
      <>
        <CircleNotch size={12} className="animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Starting sandbox…</span>
      </>
    );
  })();

  const busy = run.isPending || pendingRun;
  const canRun = !!snippet.trim() && !busy && enabled && state !== 'error';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-xs text-muted-foreground">Sandbox playground</Label>
        <span className="text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground/60">
          smoke-test
        </span>
      </div>
      <p className="text-[length:var(--text-2xs)] text-muted-foreground/80">
        Same path custom-tool handlers use. Snippets are ephemeral — nothing is stored.
      </p>

      <div className="flex items-center gap-2 text-[length:var(--text-2xs)] mb-3 px-2 py-1.5 rounded-md bg-card/50 border border-border min-h-[28px]">
        {statusContent}
      </div>

      <div className="flex items-center gap-2 mb-2 text-[length:var(--text-2xs)]">
        <span className="text-muted-foreground">Preset:</span>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              setSnippet(p.snippet);
              setLanguage(p.language);
            }}
            className="px-2 py-0.5 rounded-full border border-border bg-card hover:bg-muted text-foreground/80 hover:text-foreground"
          >
            {p.label}
          </button>
        ))}
      </div>

      <textarea
        value={snippet}
        onChange={(e) => setSnippet(e.target.value)}
        rows={5}
        className="w-full text-xs font-mono rounded-md border border-input bg-transparent px-2 py-1.5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
        placeholder={language === 'python' ? 'print("hello")' : 'echo hello'}
        spellCheck={false}
      />

      <div className="flex items-center gap-3 mt-2">
        <RadioGroup
          value={language}
          onValueChange={(v) => setLanguage(v as 'shell' | 'python')}
          className="flex items-center gap-3 w-auto"
        >
          <Label htmlFor="sandbox-lang-shell" className="flex items-center gap-1.5 text-[length:var(--text-2xs)] cursor-pointer">
            <RadioGroupItem id="sandbox-lang-shell" value="shell" />
            shell
          </Label>
          <Label htmlFor="sandbox-lang-python" className="flex items-center gap-1.5 text-[length:var(--text-2xs)] cursor-pointer">
            <RadioGroupItem id="sandbox-lang-python" value="python" />
            python
          </Label>
        </RadioGroup>
        <div className="flex-1" />
        <Button size="sm" onClick={onRun} disabled={!canRun}>
          {busy ? (
            <CircleNotch size={12} className="animate-spin mr-1" />
          ) : (
            <Play size={12} className="mr-1" />
          )}
          {run.isPending ? 'Running…' : pendingRun ? 'Starting sandbox…' : 'Run in sandbox'}
        </Button>
      </div>

      {result && (
        <div className="mt-3 rounded-md border border-border bg-card/50 overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-1.5 text-[length:var(--text-2xs)] border-b border-border bg-background/40">
            <span
              className={
                result.exitCode === 0 ? 'text-success font-medium' : 'text-destructive font-medium'
              }
            >
              exit {result.exitCode}
            </span>
            <span className="text-muted-foreground">{result.durationMs}ms</span>
            {result.timedOut && <span className="text-destructive">timed out</span>}
            {result.truncated && <span className="text-amber-500">truncated</span>}
            <span className="text-muted-foreground/70">· {result.language}</span>
          </div>
          {result.stdout && (
            <pre className="text-[length:var(--text-2xs)] font-mono px-3 py-2 whitespace-pre-wrap break-words max-h-48 overflow-auto text-foreground/90">
              {result.stdout}
            </pre>
          )}
          {result.stderr && (
            <pre className="text-[length:var(--text-2xs)] font-mono px-3 py-2 whitespace-pre-wrap break-words max-h-48 overflow-auto border-t border-border text-destructive/90">
              {result.stderr}
            </pre>
          )}
          {!result.stdout && !result.stderr && (
            <p className="text-[length:var(--text-2xs)] px-3 py-2 text-muted-foreground italic">(no output)</p>
          )}
        </div>
      )}
    </div>
  );
}
